import type { ColDef, ValueGetterParams } from "ag-grid-community";

import {
  TimestampCell,
  timestampCellClass,
  type TimestampVariant,
} from "./Cells/TimestampCell";

/** Column options shared by both `accessor` overloads. */
type AccessorOptions<TData, TValue> = Omit<
  ColDef<TData, TValue>,
  "field" | "valueGetter"
>;

/** The instant-like value a timestamp cell can render. */
type TimestampValue = string | number | Date;

interface ColumnHelper<TData> {
  /**
   * Type-checked accessor by field name. The cell renderer's `value` is typed
   * as `TData[K]`.
   */
  accessor<K extends keyof TData & string>(
    field: K,
    opts?: AccessorOptions<TData, TData[K]>,
  ): ColDef<TData, TData[K]>;

  /**
   * Type-checked accessor by function, for values derived from a row rather
   * than read off a single field (e.g. `duration` from a start/end pair).
   * Mirrors Tanstack's `accessor((row) => …, { id })`: with no field name to
   * infer an identity from, `colId` is required.
   *
   * The function is only called for rows that have loaded; AG Grid invokes
   * `valueGetter` with no data for loading and group rows, which yields
   * `undefined` without the accessor having to guard.
   */
  accessor<TValue>(
    accessorFn: (row: TData) => TValue,
    opts: AccessorOptions<TData, TValue> & { colId: string },
  ): ColDef<TData, TValue>;

  /**
   * Column for a timestamp field, pre-wired with `TimestampCell` and
   * `timestampCellClass`. `variant` selects the display — `precise` (default)
   * or the adaptive `relative`.
   *
   * The cell accepts an ISO 8601 string or a `Date`. For any other wire format
   * (e.g. nanosecond `bigint` event times), pass a `valueGetter` that
   * normalizes the row into a `Date` — the caller owns parsing since it knows
   * the field's format.
   *
   * No comparator is set, so sorting is left to the grid/consumer: a
   * client-side table can pass one via `opts`, while `DataTable` (the current
   * consumer) sorts server-side and neutralizes client reordering with its own
   * default `comparator: () => 0`.
   */
  timestamp<K extends keyof TData & string>(
    field: K,
    // Opts are typed against the instant value the cell consumes
    // (`string | number | Date`), not `TData[K]` — so a `valueGetter` may
    // normalize any wire format (e.g. nanosecond `bigint`) into a `Date`.
    opts?: Omit<
      ColDef<TData, TimestampValue>,
      "field" | "cellRenderer" | "cellClass"
    > & { variant?: TimestampVariant },
  ): ColDef<TData, TData[K]>;
}

/**
 * Ergonomic factories for column shapes. Returns plain AG Grid `ColDef`s,
 * so callers can mix helper output freely with hand-written column defs
 * when they need features the helper doesn't shortcut.
 *
 * Inspired by Tanstack Table's createColumnHelper
 */
export function createColumnHelper<TData>(): ColumnHelper<TData> {
  function accessor<TValue>(
    accessor: string | ((row: TData) => TValue),
    opts?: AccessorOptions<TData, TValue>,
  ): ColDef<TData, TValue> {
    if (typeof accessor === "function") {
      return {
        ...opts,
        valueGetter: (params: ValueGetterParams<TData, TValue>) =>
          params.data === undefined
            ? // AG Grid types the getter as returning TValue, but calls it with
              // no data for rows that haven't loaded; undefined is the value it
              // already uses for an absent cell.
              (undefined as TValue)
            : accessor(params.data),
      };
    }

    // ColDef.field is typed as the recursive nested-path union ColDefField<TData, TValue>,
    // so a plain string key can't be assigned without going through unknown.
    return { field: accessor, ...opts } as unknown as ColDef<TData, TValue>;
  }

  function timestamp<K extends keyof TData & string>(
    field: K,
    opts?: Omit<
      ColDef<TData, TimestampValue>,
      "field" | "cellRenderer" | "cellClass"
    > & { variant?: TimestampVariant },
  ): ColDef<TData, TData[K]> {
    const { variant = "precise", ...colDef } = opts ?? {};
    return {
      field,
      cellRenderer: TimestampCell,
      cellClass: timestampCellClass,
      cellRendererParams: { variant },
      ...colDef,
    } as unknown as ColDef<TData, TData[K]>;
  }

  return { accessor, timestamp };
}
